// src/components/shared/SharedHeader.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Menu, 
  X, 
  Search, 
  ShoppingCart, 
  Heart, 
  Share2, 
  MessageCircle, 
  User, 
  LogIn, 
  LogOut,
  Settings,
  Bell,
  Grid,
  List,
  Filter,
  SlidersHorizontal,
  ChevronDown,
  Eye,
  Globe,
  Lock,
  Users,
  School,
  FileText,
  BookOpen,
  Calendar,
  Building2,
  ChevronRight,
  Phone,
  Image,
  LayoutGrid,
  Rows,
  Mic,
  Volume2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ShareModal } from '../books/modals/ShareModal';
import { AlbumVoicePlayer } from '../album/voice/AlbumVoicePlayer';
import { useCart } from '../../contexts/CartContext';
import { SearchBar } from './SearchBar';
import { AnalyticsPanel } from './AnalyticsPanel';
import { AuthModal } from '../auth/AuthModal';
import { useClaims } from '../../hooks/useClaims';


// Institution section maps
const INSTITUTION_SECTION_ICON_MAP = {
  hero: School,
  about: FileText,
  leadership: Users,
  programs: BookOpen,
  events: Calendar,
  news: Bell,
  resources: FileText,
  galleries: Image,
  facilities: Building2,
  admissions: User,
  contact: Phone
};

const INSTITUTION_SECTION_LABEL_MAP = {
  hero: 'Home',
  about: 'About',
  leadership: 'Leadership',
  programs: 'Programs',
  events: 'Events',
  news: 'News',
  resources: 'Resources',
  galleries: 'Gallery',
  facilities: 'Facilities',
  admissions: 'Admissions',
  contact: 'Contact'
};

const ECOMMERCE_ACTIONS = {
  cart: { icon: ShoppingCart, label: 'Cart', showBadge: true },
  wishlist: { icon: Heart, label: 'Wishlist', showBadge: true },
  contact: { icon: MessageCircle, label: 'Contact' },
  search: { icon: Search, label: 'Search' }
};

/**
 * SharedHeader - Unified header component for all templates
 * Supports: Album, Profile, Institution, Catalog, Events, Booking, Ecommerce templates
 */
export const SharedHeader = ({
  templateType = 'album',
  bookId = null,
  bookData = {},
  styling = {},
  isOwner = false,
  navigate,
  headerConfig = {},
  actions = {},
  state = {},
  children,
  // Institution-specific props
  institutionSections = [],
  activeInstitutionSection = 'hero',
  onInstitutionSectionChange = null,
  // Gallery/View mode props
  galleries = [],
  activeGalleryId = null,
  onGalleryChange = null,
  viewMode = 'grid',
  onViewModeChange = null,
  categories = [],
  selectedCategory = 'all',
  onCategorySelect = null,
  cartItemCount = 0,
  wishlistCount = 0,
  onCartClick = null,
  onWishlistClick = null,
  onContactClick = null,
  onSearchClick = null,
  storeType = 'general',
  // Share tracking
  enableShareTracking = false,
  setShowProfileModal
}) => {
  const { user, userProfile, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const searchQuery = state?.searchQuery || '';
  const setSearchQuery = actions?.onSearchChange || (() => {});
  const { 
      permissions, 
      bookRole, 
      loading: claimsLoading,
      isOwner: isClaimsOwner,
      isBookAdmin 
    } = useClaims(bookId);
  
  // Share Modal State
  const [showShareModal, setShowShareModal] = useState(false);
  const [showAlbumVoice, setShowAlbumVoice] = useState(false);

  const [showAuthModal, setShowAuthModal] = useState(false);

  const { position, transparent, blur, opacity } = headerConfig;
  

  const isServiceStore = storeType === 'services';
  // Extract header settings from nested structures
  const getHeaderSettings = () => {
    return (
      bookData?.institutionSettings?.headerSettings ||
      bookData?.albumSettings?.headerSettings ||
      bookData?.profileSettings?.headerSettings ||
      bookData?.catalogSettings?.headerSettings ||
      bookData?.ecommerceSettings?.headerSettings ||
      bookData?.headerSettings ||
      {}
    );
  };

  const headerSettings = getHeaderSettings();

  // Get layout settings
  const layoutSettings = bookData?.layoutSettings || {};
  
  // Determine header positioning based on layoutSettings
  const headerHeroIntegration = layoutSettings.headerHeroIntegration || 'normal';
  const isOverlayMode = headerHeroIntegration === 'overlay';
  const enableSearch = headerSettings?.showSearch ?? headerConfig?.showSearch ?? true;

  // Get header styling options for overlay mode
  const headerTransparent = isOverlayMode && (layoutSettings.headerTransparentOnHero ?? true);
  const headerBlur = isOverlayMode && (layoutSettings.headerBlurOnHero ?? true);
  const headerOpacity = isOverlayMode ? (layoutSettings.headerOpacityOnHero ?? 90) : 100;

  const voiceSettings = bookData?.voiceSettings;
  const voicePermissions = voiceSettings?.albumNarration?.permissions?.canRecord;

  // Helper function to get setting value with proper fallback chain
  const getSetting = (key, templateDefault = true) => {
    if (headerSettings[key] !== undefined) {
      return headerSettings[key];
    }
    if (headerConfig[key] !== undefined) {
      return headerConfig[key];
    }
    return templateDefault;
  };

  // Calculate header dimensions from settings
  const headerHeight = getSetting('headerHeight', 80);
  const logoHeight = getSetting('logoHeight', 40);
  const headerLayout = getSetting('layout', 'default');

  const headerPosition = isOverlayMode 
    ? 'absolute'  // Float over hero
    : (layoutSettings.headerVariant || getSetting('position', 'sticky')); // Normal flow

  const getHeaderClasses = () => {
    if (isOverlayMode) {
      return 'absolute top-0 left-0 right-0 z-50';
    }
    
    switch (headerPosition) {
      case 'sticky':
        return 'sticky top-0 z-50';
      case 'fixed':
        return 'fixed top-0 left-0 right-0 z-50';
      case 'static':
        return 'relative z-50';
      default:
        return 'sticky top-0 z-50';
    }
  };

  const getHeaderStyles = () => {
    const baseColor = styling.headerBackgroundColor || '#ffffff';
    const globalOpacity = styling.headerOpacity ?? 100;
    
    // Convert hex color to rgba with opacity
    const hexToRgba = (hex, opacity) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    };
    
    if (isOverlayMode) {
      if (headerTransparent) {
        // Transparent with blur
        return {
          minHeight: `${headerHeight}px`,
          backgroundColor: `rgba(255, 255, 255, ${headerOpacity / 100})`,
          backdropFilter: headerBlur ? 'blur(8px)' : 'none',
          WebkitBackdropFilter: headerBlur ? 'blur(8px)' : 'none',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        };
      } else {
        // Semi-transparent solid background
        return {
          minHeight: `${headerHeight}px`,
          backgroundColor: hexToRgba(baseColor, headerOpacity),
          borderColor: `${baseColor}20`
        };
      }
    }
    
    // Normal flow - apply opacity only to background color
    return {
      minHeight: `${headerHeight}px`,
      backgroundColor: hexToRgba(baseColor, globalOpacity),
      borderColor: `${baseColor}20`
    };
  };
    
  // Determine user profile visibility
  const shouldShowUserProfile = () => {
    const visibility = getSetting('userProfileVisibility', 'auto');
    
    switch (visibility) {
      case 'never':
        return false;
      case 'always':
        return true;
      case 'authenticated':
        return !!user;
      case 'auto':
      default:
        if (getSetting('showProfileForPublic', false) && bookData?.accessLevel === 'public') {
          return true;
        }
        if (bookData?.accessLevel === 'private') {
          return true;
        }
        if (getSetting('requireLoginToView', false)) {
          return true;
        }
        return !!user;
    }
  };

  // Handle search with live search support
  useEffect(() => {
    if (getSetting('enableLiveSearch', true) && searchQuery !== state.searchQuery) {
      const debounce = setTimeout(() => {
        actions.onSearchChange?.(searchQuery);
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchQuery, getSetting, actions, state.searchQuery]);

  const getCartIconAndLabel = () => {
    if (templateType !== 'ecommerce') {
      return { Icon: ShoppingCart, label: 'Cart', ariaLabel: 'Shopping cart' };
    }

    if (isServiceStore) {
      return { 
        Icon: Calendar, 
        label: 'Bookings', 
        ariaLabel: 'Service bookings',
        badgeColor: '#10b981' // Green for bookings
      };
    }

    return { 
      Icon: ShoppingCart, 
      label: 'Cart', 
      ariaLabel: 'Shopping cart',
      badgeColor: styling.primaryColor || '#3b82f6' // Blue for cart
    };
  };

  // Institution Navigation Component
  const InstitutionNavigation = () => {
    if (templateType !== 'institution' || !institutionSections.length) {
      return null;
    }

    const visibleSections = institutionSections.filter(sectionId => {
      const visibility = bookData?.institutionSettings?.sectionVisibility?.[sectionId];
      return visibility !== false;
    });

    return (
      <>
        <div className="hidden md:flex space-x-1">
          {visibleSections.map((sectionId) => {
            const Icon = INSTITUTION_SECTION_ICON_MAP[sectionId];
            const isActive = activeInstitutionSection === sectionId;
            
            if (!Icon) return null;
            
            return (
              <button
                key={sectionId}
                onClick={() => onInstitutionSectionChange?.(sectionId)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-white bg-opacity-20' 
                    : 'hover:bg-white hover:bg-opacity-10'
                }`}
                style={{
                  color: isActive 
                    ? styling.primaryColor || styling.headerTextColor || '#1f2937'
                    : styling.headerTextColor || '#6b7280'
                }}
              >
                <Icon className="h-4 w-4" />
                <span>{INSTITUTION_SECTION_LABEL_MAP[sectionId]}</span>
              </button>
            );
          })}
        </div>

        {mobileMenuOpen && (
          <div className="border-t mt-2 pt-2">
            <div className="space-y-1">
              {visibleSections.map((sectionId) => {
                const Icon = INSTITUTION_SECTION_ICON_MAP[sectionId];
                const isActive = activeInstitutionSection === sectionId;
                
                if (!Icon) return null;
                
                return (
                  <button
                    key={sectionId}
                    onClick={() => {
                      onInstitutionSectionChange?.(sectionId);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-white bg-opacity-20' 
                        : 'hover:bg-white hover:bg-opacity-10'
                    }`}
                    style={{
                      color: styling.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor || '#1f2937'
                    }}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{INSTITUTION_SECTION_LABEL_MAP[sectionId]}</span>
                    {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  };

  // Ecommerce Category Navigation Component
  const EcommerceCategoryNav = () => {
    if (templateType !== 'ecommerce' || !categories?.length) {
      return null;
    }

    // Check if category navigation should be shown
    if (!getSetting('showCategoryNav', true)) {
      return null;
    }

    const visibleCategories = categories.filter(cat => cat.status === 'active');
    const displayLimit = getSetting('categoryDisplayLimit', 6);

    return (
      <div className="hidden lg:flex items-center gap-1 overflow-x-auto">
        <button
          onClick={() => onCategorySelect?.('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors `} 
          style={{
            backgroundColor: selectedCategory === 'all' ? bookData?.design.accentColor : 'transparent',
            color:  bookData.headerTextColor || bookData?.styling?.headerTextColor || bookData?.styling?.headerTextColor
          }}
        >
          All Products
        </button>
        {visibleCategories.slice(0, displayLimit).map(category => (
          <button
            key={category.id}
            onClick={() => onCategorySelect?.(category.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors `}
              style={{
              backgroundColor: 'transparent',
              color:  bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor 
            }}
          >
            {category.name}
          </button>
        ))}
        {visibleCategories.length > displayLimit && (
          <button
            className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 whitespace-nowrap"
          style={{ color:  bookData.headerTextColor }}
          >
            More <ChevronDown className="h-4 w-4 inline ml-1" />
          </button>
        )}
      </div>
    );
  };

  // Ecommerce Actions Component
  const EcommerceActions = () => {
    if (templateType !== 'ecommerce') {
      return null;
    }

    const { Icon: CartIcon, label: cartLabel, ariaLabel: cartAriaLabel, badgeColor } = getCartIconAndLabel();

    return (
      <div className="flex items-center gap-2">
        {/* Search Button */}
        {getSetting('showSearch', true) && (
          <button
            onClick={() => onSearchClick?.()}
            className="p-2 rounded-lg hover:bg-gray-100 relative"
            aria-label="Search products"
          >
            <Search className="h-5 w-5" />
          </button>
        )}

        {/* Wishlist Button */}
        {getSetting('showWishlist', true) && (
          <button
            onClick={() => onWishlistClick?.()}
            className="p-2 rounded-lg hover:bg-gray-100 relative"
            style={{ color: bookData.headerTextColor }}
            aria-label="Wishlist"
          >
            <Heart className="h-5 w-5" />
            {wishlistCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-medium text-white flex items-center justify-center"
                style={{ backgroundColor: styling.primaryColor || '#3b82f6', font:bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
              >
                {wishlistCount}
              </span>
            )}
          </button>
        )}

        {/* ✅ UPDATED: Cart/Bookings Button - Smart Icon */}
        {getSetting('showCart', true) && (
          <button
            onClick={() => onCartClick?.()}
            className="p-2 rounded-lg hover:bg-gray-100 relative"
            style={{ color: bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
            aria-label={cartAriaLabel}
            title={isServiceStore ? 'View your bookings' : 'View shopping cart'}
          >
            <CartIcon className="h-5 w-5" />
            {cartItemCount > 0 && (
              <span 
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-medium text-white flex items-center justify-center shadow-lg"
                style={{ backgroundColor: badgeColor }}
              >
                {cartItemCount}
              </span>
            )}
          </button>
        )}

        {/* Contact Button - Desktop */}
        {getSetting('showContact', true) && (
          <button
            onClick={() => onContactClick?.()}
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 text-sm font-medium"
            style={{ color: bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
          >
            <MessageCircle className="h-4 w-4" />
            Contact
          </button>
        )}
      </div>
    );
  };

  // Mobile Ecommerce Menu Component
  const MobileEcommerceMenu = () => {
    if (templateType !== 'ecommerce') {
      return null;
    }

    const { Icon: CartIcon, label: cartLabel, badgeColor } = getCartIconAndLabel();

    return (
      <div className="px-3 space-y-3">
        {/* Categories Section */}
        {getSetting('showCategoryNav', true) && categories?.length > 0 && (
          <div className="border-t border-gray-200 pt-3">
            <h4 className="text-xs font-semibold uppercase mb-2"
              style={{ color: bookData?.design.headerTextColor || bookData?.styling?.headerTextColor }}>
              {isServiceStore ? 'Service Types' : 'Categories'}
            </h4>
            <nav className="space-y-2">
              <button
                onClick={() => {
                  onCategorySelect?.('all');
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-900 hover:bg-gray-100'
                }`}
              >
                {isServiceStore ? 'All Services' : 'All Products'}
              </button>
              {categories?.filter(cat => cat.status === 'active').map(category => (
                <button
                  key={category.id}
                  onClick={() => {
                    onCategorySelect?.(category.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left py-2 px-3 rounded-lg transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Quick Actions */}
        <div className="border-t border-gray-200 pt-3 space-y-2">
          {getSetting('showContact', true) && (
            <button
              onClick={() => {
                onContactClick?.();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 w-full py-2 px-3 rounded-lg hover:bg-gray-100"
              style={{ color: bookData?.design.headerTextColor || bookData?.styling?.headerTextColor }}
            >
              <MessageCircle className="h-5 w-5" />
              <span>Contact Us</span>
            </button>
          )}
          
          {getSetting('showWishlist', true) && (
            <button
              onClick={() => {
                onWishlistClick?.();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 w-full py-2 px-3 rounded-lg hover:bg-gray-100"
              style={{ color: bookData?.design.headerTextColor || bookData?.styling?.headerTextColor }}
            
            >
              <Heart className="h-5 w-5" />
              <span>Wishlist</span>
              {wishlistCount > 0 && (
                <span className="ml-auto bg-gray-200 text-xs px-2 py-1 rounded-full"
                style={{ color: bookData?.design.headerTextColor || bookData?.styling?.headerTextColor }}
                >
                  {wishlistCount}
                </span>
              )}
            </button>
          )}
          
          {/* ✅ UPDATED: Cart/Bookings Mobile Link */}
          {getSetting('showCart', true) && (
            <button
              onClick={() => {
                onCartClick?.();
                setMobileMenuOpen(false);
              }}
              className="flex items-center gap-3 w-full py-2 px-3 rounded-lg hover:bg-gray-100 text-gray-900"
            >
              <CartIcon className="h-5 w-5"/>
              <span>{cartLabel}</span>
              {cartItemCount > 0 && (
                <span 
                  className="ml-auto text-white text-xs px-2 py-1 rounded-full font-medium"
                  style={{ backgroundColor: badgeColor }}
                >
                  {cartItemCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Header Branding Component
  const HeaderBranding = () => {
    const [isSquareImage, setIsSquareImage] = useState(true);
    
    const handleImageLoad = (e) => {
      const { naturalWidth, naturalHeight } = e.target;
      setIsSquareImage(naturalWidth === naturalHeight);
    };

    const showLogo = getSetting('showLogo', true);
    const showTitle = getSetting('showTitle', true);
    const showDescription = getSetting('showDescription', false);
    const showOwnerControls = getSetting('showOwnerControls', true);

    return (
      <div className="flex items-center gap-2">
        {isOwner && showOwnerControls && navigate && (
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Return to Dashboard"
          >
            <ArrowLeft className="h-5 w-5" 
            style={{ color:  bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
            />
          </button>
        )}

        {showLogo && bookData?.logoUrl && (
          <div className="flex-shrink-0">
            <img
              src={bookData.logoUrl}
              alt={`${bookData.botName || bookData.title} logo`}
              style={{ height: `${logoHeight}px`,
                       filter: `invert(${bookData?.styling?.logoInvert || 0}%)`
              }}
              className="w-auto object-contain rounded-lg"
              onLoad={handleImageLoad}
            />
          </div>
        )}

        {showTitle && (isSquareImage || !bookData?.logoUrl) && (
          <div className="min-w-0 flex-1">
            <h1
              className="text-xl font-bold text-gray-900 truncate"
              style={{ color: styling.primaryColor || styling.headerTextColor }}
            >
              {bookData.botName || bookData.title || 'Untitled'}
            </h1>
            {showDescription && bookData.description && (
              <p className="text-sm text-gray-600 truncate">
                {bookData.description}
              </p>
            )}
          </div>
        )}

      </div>
    );
  };

  // Mobile Access Level Badge Component
  const MobileAccessLevelBadge = () => {
    if (!bookData?.accessLevel) return null;

    const accessConfig = {
      public: { icon: Globe, label: 'Public', color: 'bg-green-100 text-green-800' },
      private: { icon: Lock, label: 'Private', color: 'bg-red-100 text-red-800' },
      restricted: { icon: Users, label: 'Restricted', color: 'bg-yellow-100 text-yellow-800' },
      organization: { icon: Users, label: 'Organization', color: 'bg-blue-100 text-blue-800' }
    };

    const config = accessConfig[bookData.accessLevel] || accessConfig.public;
    const Icon = config.icon;

    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${config.color}`}>
        <Icon className="h-4 w-4" />
        <span className="capitalize">{config.label}</span>
      </div>
    );
  };

  // Mobile User Profile Component
  const MobileUserProfile = () => {
    const showProfile = shouldShowUserProfile();
    
    if (!showProfile) return null;

    return (
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2"
        style={{ color: bookData?.styling?.headerTextColor }}
        >Account</h4>
        
        {user ? (
          <>
            <div className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
              {/* Analytics Panel for Owner */}
              {isOwner && bookData?.customDomain && (
                <AnalyticsPanel bookId={bookData.id || bookData.bookId} compact={true} />
              )}
              {userProfile?.photoURL ? (
                <img
                  src={userProfile.photoURL}
                  alt={userProfile.displayName}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-white"
                />
              ) : (
                <div 
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-medium text-lg"
                  style={{ backgroundColor: styling.primaryColor || '#6366f1' }}
                >
                  {(userProfile?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {userProfile?.displayName || 'User'}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {userProfile?.email || user?.email}
                </p>
              </div>
            </div>

            {isOwner && navigate && (
              <button
                onClick={() => {
                  navigate('/dashboard');
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings className="h-4 w-4" />
                <span>Dashboard</span>
              </button>
            )}

            <button
              onClick={() => {
                if (navigate) {
                  navigate('/profile');
                } else {
                  actions.onProfile?.();
                }
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
              style={{ color:  bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
            >
              <User className="h-4 w-4" />
              <span>My Profile</span>
            </button>

            <button
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </>
        ) : (
          <button
            onClick={() => {
              if (navigate) {
                navigate('/login');
              } else {
                actions.onLogin?.();
              }
              setMobileMenuOpen(false);
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm text-white"
            style={{ backgroundColor: styling.primaryColor || '#6366f1' }}
          >
            <LogIn className="h-4 w-4" />
            <span>Sign In</span>
          </button>
        )}
      </div>
    );
  };

  // Access Level Badge Component
  const AccessLevelBadge = () => {
    const showAccessLevel = getSetting('showAccessLevel', true);
    
    if (!showAccessLevel || !bookData?.accessLevel) {
      return null;
    }

    const accessConfig = {
      public: { icon: Globe, label: 'Public', color: 'bg-green-100 text-green-800' },
      private: { icon: Lock, label: 'Private', color: 'bg-red-100 text-red-800' },
      restricted: { icon: Users, label: 'Restricted', color: 'bg-yellow-100 text-yellow-800' },
      organization: { icon: Users, label: 'Organization', color: 'bg-blue-100 text-blue-800' }
    };

    const config = accessConfig[bookData.accessLevel] || accessConfig.public;
    const Icon = config.icon;

    return (
      <div className={`hidden sm:flex items-center gap-2 px-3 py-1 rounded-full text-sm ${config.color}`}>
        <Icon className="h-4 w-4" />
        <span className="capitalize">{config.label}</span>
      </div>
    );
  };

  // Share Button Component
  const ShareButton = () => {
    const showShareButton = getSetting('showShareButton', true);
    
    if (!showShareButton) {
      return null;
    }

    return (
      <button
        onClick={() => setShowShareModal(true)}
      >
        <Share2 className="h-5 w-5" style={{ color: bookData?.styling?.headerTextColor || bookData?.design?.headerTextColor || '#0795ff' }} />
        <span className="hidden sm:inline"></span>
      </button>
    );
  };

  // Album-specific Collapsible Controls Component
  const AlbumControls = () => {
    const showGallerySelector = getSetting('showGallerySelector', true);
    const showViewModeToggle = getSetting('showViewModeToggle', true);
    const showOwnerControls = getSetting('showOwnerControls', true);
    
    if (templateType !== 'album' || !getSetting('showFilterControls', true)) return null;
    
    return (
      <div
        className={`${
          state.showFilters ? 'flex' : 'hidden'
        } flex-col lg:flex-row gap-4 items-start lg:items-center justify-between border-t border-gray-100 pt-4 mt-2`}
      >
        {/* Gallery Selector */}
        {showGallerySelector && (
          <div className="w-full sm:w-64">
            <label htmlFor="gallerySelect" className="block text-sm mb-1"
            style={{ color:  bookData.headerTextColor || bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor }}
            >
              Gallery
            </label>
            <select
              id="gallerySelect"
              value={state.activeGalleryId || ''}
              onChange={(e) => actions.onGalleryChange?.(e.target.value || null)}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:border-blue-500"
              style={{ '--tw-ring-color': styling.primaryColor }}
            >
              <option value="">All Galleries</option>
              {state.galleries?.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name || g.title || g.id}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full lg:w-auto">
          {/* Mobile Search */}
          {getSetting('showSearch', true) && getSetting('showSearchOnMobile', false) && (
            <div className="relative block lg:hidden">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={getSetting('searchPlaceholder', 'Search media...')}
                value={state.searchQuery || ''}
                onChange={(e) => actions.onSearchChange?.(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-blue-500 w-full sm:w-64"
                style={{ '--tw-ring-color': styling.primaryColor }}
              />
            </div>
          )}

          {/* Filter Controls */}
          <div className="flex gap-2">
            <select
              value={state.filterBy || 'all'}
              onChange={(e) => actions.onFilterChange?.(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2"
              style={{ '--tw-ring-color': styling.primaryColor }}
            >
              <option value="all">All Media</option>
              <option value="images">Images Only</option>
              <option value="videos">Videos Only</option>
            </select>

            <select
              value={state.sortBy || 'date'}
              onChange={(e) => actions.onSortChange?.(e.target.value)}
              className="px-6 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2"
              style={{ '--tw-ring-color': styling.primaryColor }}
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="type">Sort by Type</option>
            </select>
          </div>

          {/* Owner Controls */}
          {(isOwner || permissions.canEdit) && showOwnerControls && (
            <button
              onClick={() => actions.onCreateStory?.()}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
            >
              <Eye className="h-4 w-4" />
              Create Story
            </button>
          )}
        </div>
      </div>
    );
  };

  // Dynamic Voice Button Component (Record or Play)
  const VoiceButton = () => {
    const hasAlbumVoice = bookData?.albumVoice?.url;
    const voiceSettings = bookData?.voiceSettings;
    
    // Check if voice features are enabled
    const voiceEnabled = voiceSettings?.albumNarration?.enabled;
    
    if (templateType !== 'album' || !voiceEnabled) {
      return null;
    }

    const handleVoiceAction = () => {
      if (hasAlbumVoice) {
        // Play existing album voice
        setShowAlbumVoice(true);
      } else if (isOwner) {
        // Trigger recording modal (only for owners)
        actions.onRecordAlbumVoice?.();
      }
    };

    return (
      <button
        onClick={handleVoiceAction}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
          hasAlbumVoice 
            ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100' 
            : 'border-gray-300 bg-purple-600 text-white hover:bg-gray-50'
        }`}
        title={hasAlbumVoice ? 'Play Album Voice' : 'Record Album Voice'}
      >
        {hasAlbumVoice ? (
          <>
            <Volume2 className="h-4 w-4" />
          </>
        ) : (
          <>
            <Mic className="h-4 w-4" />
          </>
        )}
      </button>
    );
  };

  // Header Actions Component
  const HeaderActions = () => {
    const showFilterControls = getSetting('showFilterControls', true);
    
    return (
      <div className="flex items-center gap-2">
        <AccessLevelBadge />
        
        {/* Filter Toggle Button - Only for Album */}
        {templateType === 'album' && showFilterControls && (
          <button
            onClick={() => actions.onToggleFilters?.()}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-sm ${
              state.showFilters 
                ? 'border-blue-300 bg-blue-50 text-blue-700' 
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">
              {state.showFilters ? 'Hide' : 'Show'}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${state.showFilters ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
    );
  };

  // User Profile Component
  const UserProfile = () => {
    const showProfile = shouldShowUserProfile();
    
    if (!showProfile) return null;

    return (
      <div className="flex items-center gap-2">
        {user ? (
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {userProfile?.photoURL ? (
                <img
                  src={userProfile.photoURL}
                  alt={userProfile.displayName}
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-white"
                />
              ) : (
                <div 
                  className="h-8 w-8 rounded-full flex items-center justify-center text-white font-medium"
                  style={{ backgroundColor: styling.primaryColor || '#6366f1' }}
                >
                  {(userProfile?.displayName?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                </div>
              )}
              <ChevronDown className="h-4 w-4 text-gray-600 hidden md:block" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                {/* Analytics Panel for Owner */}
                {isOwner && bookData?.customDomain && (
                  <AnalyticsPanel bookId={bookData.id || bookData.bookId} compact={true} />
                )}
                <div className="p-3 border-b border-gray-200">
                  <p className="font-medium text-gray-900 truncate">
                    {userProfile?.displayName || 'User'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {userProfile?.email || user?.email}
                  </p>
                </div>
                <div className="py-1">
                  {isOwner && navigate && (
                    <>
                      <button
                        onClick={() => {
                          navigate('/dashboard');
                          setShowUserMenu(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Settings className="h-4 w-4" />
                        Dashboard
                      </button>
                      <div className="border-t border-gray-200 my-1"></div>
                    </>
                  )}
                  <button
                            onClick={() => {
                              setShowProfileModal(true);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg"
                            
                          >
                            <User className="h-4 w-4" />
                            My Profile
                          </button>
                  <button
                    onClick={() => {
                      logout();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowAuthModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium text-sm"
            style={{
              backgroundColor: styling.primaryColor || '#6366f1',
              color: '#ffffff'
            }}
          >
            <LogIn className="h-4 w-4" />
            <span>Sign In</span>
          </button>
        )}
      </div>
    );
  };

  // Determine header position class
  const positionClass = headerPosition === 'sticky' ? 'sticky top-0' : 
                       headerPosition === 'fixed' ? 'fixed top-0 left-0 right-0' : '';

  return (
    <>
      <header
        className={`${getHeaderClasses()} transition-all duration-300`}
        style={getHeaderStyles()}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-2 lg:px-8">
          <div className="flex justify-between items-center" style={{ minHeight: `${headerHeight}px` }}>
            <div className="flex items-center gap-0 flex-shrink-0">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                style={{ color: bookData?.design?.headerTextColor || bookData?.styling?.headerTextColor ||'#0795ff' }}
                aria-label="Toggle menu"  
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              <HeaderBranding />
              
            </div>

            <div className="hidden md:flex flex-1 justify-center mx-4">
              {templateType === 'institution' ? (
                <InstitutionNavigation />
              ) : templateType === 'ecommerce' ? (
                <EcommerceCategoryNav />
              ) : (
                enableSearch && (
                  <SearchBar
                    styling={styling}
                    headerSettings={headerConfig}
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onSubmit={actions.onSearchSubmit}
                    actions={actions}
                  />
                )
              )}
            </div>

            <div className="flex px-2 gap-3">
              <div className="hidden md:block">
              {templateType === 'ecommerce' && <EcommerceActions />}
              </div>

              <ShareButton />
            
              {/* ✅Voice Speaker Button */}
              {voicePermissions && (
              <VoiceButton />
              )}
            </div>

            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              <HeaderActions />
              <UserProfile />
            </div>
          </div>

          {/* Album Collapsible Controls */}
          <div className="hidden md:block">
            <AlbumControls />
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-200 mt-2 space-y-3">

              {/* User Profile - Mobile Version */}
              <div className="px-3 border-t border-gray-200 pt-3">
                <MobileUserProfile />
              </div>
              
              {/* Search Section */}
              {templateType !== 'institution' && getSetting('showSearch', true) && (
                <div className="px-3 pt-3">
                  
                <SearchBar
                 styling={styling}
                 headerSettings={headerConfig}
                 value={searchQuery}
                 onChange={setSearchQuery}
                 onSubmit={actions.onSearchSubmit}
                 actions={actions}
                />
   
                </div>
              )}
              
              {/* Institution Navigation */}
              <div className="px-3">
                <InstitutionNavigation />
              </div>

              {templateType === 'ecommerce' && <MobileEcommerceMenu />}
              
              {/* Access Level Badge - Mobile */}
              {getSetting('showAccessLevel', true) && bookData?.accessLevel && (
                <div className="px-3">
                  <MobileAccessLevelBadge />
                </div>
              )}

              {/* Album Controls - Mobile Version */}
              {templateType === 'album' && getSetting('showFilterControls', true) && (
                <div className="px-3 space-y-3">
                  <div className="border-t border-gray-200 pt-3">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">View Options</h4>
                    
                    {/* Gallery Selector */}
                    {galleries?.length > 0 && getSetting('showGallerySelector', true) && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gallery</label>
                        <select
                          value={activeGalleryId || ''}
                          onChange={(e) => {
                            onGalleryChange?.(e.target.value || null);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">All Galleries</option>
                          {galleries.map((gallery) => (
                            <option key={gallery.id} value={gallery.id}>
                              {gallery.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* View Mode Toggle */}
                    {getSetting('showViewModeToggle', true) && (
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">View Mode</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => onViewModeChange?.('grid')}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              viewMode === 'grid'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <LayoutGrid className="h-4 w-4" />
                            Grid
                          </button>
                          <button
                            onClick={() => onViewModeChange?.('masonry')}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                              viewMode === 'masonry'
                                ? 'border-blue-500 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Grid className="h-4 w-4" />
                            Masonry
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Filter Controls */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Filters</label>
                      <select
                        value={state.filterType || 'all'}
                        onChange={(e) => actions.onFilterChange?.(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="all">All Media</option>
                        <option value="images">Images Only</option>
                        <option value="videos">Videos Only</option>
                      </select>

                      <select
                        value={state.sortBy || 'date'}
                        onChange={(e) => actions.onSortChange?.(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="date">Sort by Date</option>
                        <option value="name">Sort by Name</option>
                        <option value="type">Sort by Type</option>
                      </select>
                    </div>

                    {/* Owner Controls */}
                    {isOwner && getSetting('showOwnerControls', true) && (
                      <button
                        onClick={() => {
                          actions.onCreateStory?.();
                          setMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-3 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                      >
                        <Eye className="h-4 w-4" />
                        Create Story
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Header Actions - Mobile Version */}
              <div className="px-3 space-y-2 border-t border-gray-200 pt-3">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Actions</h4>
                
                {getSetting('showShareButton') && (
                  <button
                    onClick={() => {
                      setShowShareModal(true);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Share2 className="h-4 w-4" />
                    <span>Share</span>
                  </button>
                )}

                {getSetting('showFilterControls', true) && templateType === 'album' && (
                  <button
                    onClick={() => {
                      actions.onToggleFilters?.();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      state.showFilters 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Filter className="h-4 w-4" />
                    <span>{state.showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {children}
        </div>
      </header>

      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
          aria-hidden="true"
        />
      )}

      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        defaultView="signin" // or "signup"
      />

      {/* Share Modal */}
      <ShareModal
        content={bookData}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        trackingEnabled={enableShareTracking}
      />

      {/* ✅ ADD Album Voice Player */}
      {showAlbumVoice && bookData?.albumVoice && (
        <AlbumVoicePlayer
          albumVoice={bookData.albumVoice}
          voiceSettings={bookData.voiceSettings}
          onClose={() => setShowAlbumVoice(false)}
          autoPlay={true}
        />
      )}
    </>
  );
};

export default SharedHeader;